import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { normalizeContentfulAssetUrl } from '@sg/garnish';

import { useContentfulContentTypeEntry } from '@order/Contentful';

import type { Asset } from 'contentful';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the Contentful entry backing the bundle screen, falling back to
 * bundled copy whenever the entry is missing or Contentful is unreachable.
 */
export const useBundleContent = () => {
  const { formatMessage } = useIntl();

  const { data, fetching } = useContentfulContentTypeEntry<BundleFields>({
    contentType: BUNDLE_CONTENT_TYPE,
  });

  const fields = data?.fields;

  // ─── Derived ─────────────────────────────────────────────────────────

  const content = useMemo<BundleContent>(() => {
    if (!fields) {
      return {
        name: formatMessage(messages.name),
        description: formatMessage(messages.description),
        selectTitle: formatMessage(messages.selectTitle),
        categoryIds: FALLBACK_CATEGORY_IDS,
      };
    }

    const imageUrl = fields.image?.fields.file?.url;

    return {
      name: fields.name,
      description: fields.description,
      selectTitle: fields.selectTitle,
      categoryIds: fields.categoryIds,
      imageUrl: imageUrl ? normalizeContentfulAssetUrl(imageUrl) : undefined,
      imageA11y: fields.image?.fields.description,
    };
  }, [fields, formatMessage]);

  return { content, fetching };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const BUNDLE_CONTENT_TYPE = 'bundle';

// NOTE: Menu category ids are global, so these resolve in every restaurant. 69 is Warm Plates.
const FALLBACK_CATEGORY_IDS = ['69', '548'];

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  name: {
    defaultMessage: 'Family Meal',
    description: 'Bundle | Fallback content | Bundle name',
  },
  description: {
    defaultMessage: 'Choose four of our hearty plates. Customize them after you add to your bag.',
    description: 'Bundle | Fallback content | Bundle description',
  },
  selectTitle: {
    defaultMessage: 'Select a plate',
    description: 'Bundle | Fallback content | Select a product CTA title',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type BundleContent = {
  name: string;
  description: string;
  selectTitle: string;
  categoryIds: string[];
  imageUrl?: string;
  imageA11y?: string;
};

type BundleFields = {
  name: string;
  description: string;
  selectTitle: string;
  categoryIds: string[];
  image?: Asset;
};
